import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { createDocument, isDocIdTaken } from '@/data/util_documentIds';
import { db } from '@/firebaseInit';
import { coll_collections, makeEmptyBio, type CollectionType } from '@/types/types_collection';
import { coll_experiences } from '@/types/types_experience';

// Data-access layer for collections, backed by Firestore. Mirrors the shape of
// experiencesRepo.ts.

const collectionsCollection = collection(db, coll_collections);

// Fields a collection used to have. A stored document keeps whatever was
// written into it, so these are dropped on the way in — see `toCollection`.
const droppedCollectionFields = ['shortDescription', 'fullDescription', 'includes'];

function toCollection(id: string, data: Record<string, unknown>): CollectionType {
  // Documents written before the model dropped its description fields still
  // carry them. Peel them off rather than spreading them through, or they would
  // ride along on the next save and never leave the document.
  const rest = Object.fromEntries(
    Object.entries(data).filter(([key]) => !droppedCollectionFields.includes(key)),
  );

  // The experiences heading, in turn, was added after the first staging import,
  // so documents from then do not have it.
  const stored = rest as Omit<CollectionType, 'id' | 'experiencesTitle' | 'experiencesSubtitle'> &
    Partial<Pick<CollectionType, 'experiencesTitle' | 'experiencesSubtitle'>>;
  return {
    ...stored,
    experiencesTitle: stored.experiencesTitle ?? '',
    experiencesSubtitle: stored.experiencesSubtitle ?? '',
    // A stored bio predating a later field would otherwise arrive with holes in
    // it, which the admin form binds to as uncontrolled inputs.
    bio: stored.bio ? { ...makeEmptyBio(), ...stored.bio } : undefined,
    id,
  };
}

export async function listCollections(): Promise<CollectionType[]> {
  const snap = await getDocs(collectionsCollection);
  return snap.docs.map((d) => toCollection(d.id, d.data()));
}

export async function getCollection(id: string): Promise<CollectionType | undefined> {
  const snap = await getDoc(doc(collectionsCollection, id));
  return snap.exists() ? toCollection(snap.id, snap.data()) : undefined;
}

/**
 * Subscribes to all collections, invoking `onChange` on the initial snapshot and
 * on every change. Returns an unsubscribe function.
 */
export function subscribeCollections(
  onChange: (collections: CollectionType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    collectionsCollection,
    (snap) => onChange(snap.docs.map((d) => toCollection(d.id, d.data()))),
    onError,
  );
}

/**
 * Subscribes to a single collection by id, invoking `onChange` with the current
 * value (or undefined if it doesn't exist) on every change. Returns an
 * unsubscribe function.
 */
export function subscribeCollection(
  id: string,
  onChange: (collection: CollectionType | undefined) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(collectionsCollection, id),
    (snap) => onChange(snap.exists() ? toCollection(snap.id, snap.data()) : undefined),
    onError,
  );
}

/**
 * Whether a collection already exists under this id. For a form to show while
 * somebody types; `createCollection` is what keeps the id unique.
 */
export async function isCollectionIdTaken(id: string): Promise<boolean> {
  return isDocIdTaken(collectionsCollection, id);
}

/**
 * Creates a collection, refusing an id another collection already holds.
 *
 * Unlike an experience, a collection is edited through `saveCollection` below —
 * the form has a Save button rather than autosave — so the two paths are named
 * apart here: only this one may claim an id, and only that one may write over a
 * record that exists.
 */
export async function createCollection(coll: CollectionType): Promise<CollectionType> {
  return createDocument(collectionsCollection, coll);
}

/** Updates an existing collection, or creates it if the id is free (upsert by id). */
export async function saveCollection(coll: CollectionType): Promise<CollectionType> {
  const now = new Date().toISOString();
  const ref = doc(collectionsCollection, coll.id);

  const existing = await getDoc(ref);
  const existingData = existing.exists() ? existing.data() : undefined;

  const toSave: CollectionType = {
    ...coll,
    createdAt: (existingData?.createdAt as string | undefined) ?? now,
    updatedAt: now,
  };

  await setDoc(ref, toSave);
  return toSave;
}

/** Deletes a collection; member experiences become standalone rather than being deleted. */
export async function deleteCollection(id: string): Promise<void> {
  const experiencesCollection = collection(db, coll_experiences);
  const members = await getDocs(query(experiencesCollection, where('collectionId', '==', id)));

  const batch = writeBatch(db);
  batch.delete(doc(collectionsCollection, id));
  members.forEach((member) => batch.update(member.ref, { collectionId: null }));
  await batch.commit();
}
